<template>
  <div class="panel-card-list md:flex items-between">
    <div class="main w-full grid content-between lg:w-1/2 md:mr-4">
      <!--  航海距離 -->
      <panel-card class="mb-2" :params="accumulatedDistanceParams" />

      <!-- 燃料消費量 -->
      <panel-card class="mb-2 md:mb-0" :params="fuelConsumptions" />
    </div>
    <div class="sub w-full grid content-between lg:w-1/2">
      <!-- 航海時間 -->
      <panel-card class="mb-2" :params="accumulatedTimesParams" />

      <!-- 主機稼働率 -->
      <panel-card class="mb-2" :params="operationRateParams" />

      <!-- 対水速力・対地速力 -->
      <panel-card class="mb-2" :params="averageSpeedOvers" />

      <!-- GHG -->
      <panel-card class="mb-2 md:mb-0" :params="ghgParams" />
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
const { mapActions, mapGetters } = createNamespacedHelpers("fuelConsumption");
import moment from "moment";
import PanelCard from "./PanelCard.vue";

export default {
  components: {
    PanelCard,
  },

  computed: {
    ...mapGetters([
      "getStartPort",
      "getGoalPort",
      "getUsualRoute",
      "getRecomendedRoute",
      "getActualRoute",
      "getRouteAgreementRate",
      "getMapCenterPosition",
      "getOperationRate",
    ]),

    emptyValues() {
      return {
        value: "-",
        unit: "",
      };
    },

    accumulatedDistanceParams() {
      return [
        {
          title: "航海距離",
          values: this.accumulatedDistanceValues,
          isVarticalValues: false,
        },
      ];
    },

    accumulatedDistanceValues() {
      if (!this.getActualRoute || !this.getActualRoute.accumulated_distance)
        return [this.emptyValues];

      return [
        {
          value: this.parseComma(this.getActualRoute.accumulated_distance, 2),
          unit: "nm",
        },
      ];
    },

    accumulatedTimesParams() {
      return [
        {
          title: "航海時間",
          values: this.accumulatedTimesValues,
          isVarticalValues: true,
        },
      ];
    },

    accumulatedTimesValues() {
      if (!this.getActualRoute || !this.getActualRoute.accumulated_seconds)
        return [this.emptyValues];

      const hours = Math.floor(
        this.getActualRoute.accumulated_seconds / 60 / 60
      );
      const minutes = Math.floor(
        (this.getActualRoute.accumulated_seconds % (60 * 60)) / 60
      );

      return [
        {
          value: this.parseComma(hours, 0),
          unit: "h",
        },
        {
          value: minutes,
          unit: "m",
        },
      ];
    },

    fuelConsumptions() {
      return [
        {
          title: "燃料消費量",
          option: "合計",
          values: this.totalFuelConsumption,
          isVarticalValues: false,
        },
        {
          title: "主機",
          values: this.mainEngineFuelConsumption,
          isVarticalValues: false,
        },
        {
          title: "発電機",
          values: this.generatorFuelConsumption,
          isVarticalValues: false,
        },
        {
          title: "ボイラー",
          values: this.boilerFuelConsumption,
          isVarticalValues: false,
        },
      ];
    },

    totalFuelConsumption() {
      if (!this.getActualRoute) return [this.emptyValues];

      const mainEngine = this.getActualRoute.main_engine_fuel_consumption
        ? this.getActualRoute.main_engine_fuel_consumption
        : 0;
      const generator = this.getActualRoute.generator_fuel_consumption
        ? this.getActualRoute.generator_fuel_consumption
        : 0;
      const boiler = this.getActualRoute.boiler_fuel_consumption
        ? this.getActualRoute.boiler_fuel_consumption
        : 0;

      const total = mainEngine + generator + boiler;

      return [
        {
          value: this.parseComma(total, 1),
          unit: "L",
        },
      ];
    },
    mainEngineFuelConsumption() {
      if (
        !this.getActualRoute ||
        !this.getActualRoute.main_engine_fuel_consumption
      )
        return [this.emptyValues];

      const fuelConsumptionPerMile = this.getActualRoute.accumulated_distance
        ? this.parseComma(
            this.getActualRoute.main_engine_fuel_consumption /
              this.getActualRoute.accumulated_distance,
            1
          )
        : "-";

      return [
        {
          value: fuelConsumptionPerMile,
          unit: "L/nm",
        },
        {
          value: this.parseComma(
            this.getActualRoute.main_engine_fuel_consumption,
            1
          ),
          unit: "L",
        },
      ];
    },
    generatorFuelConsumption() {
      if (
        !this.getActualRoute ||
        !this.getActualRoute.generator_fuel_consumption
      )
        return [this.emptyValues];

      return [
        {
          value: this.parseComma(
            this.getActualRoute.generator_fuel_consumption,
            1
          ),
          unit: "L",
        },
      ];
    },
    boilerFuelConsumption() {
      if (!this.getActualRoute || !this.getActualRoute.boiler_fuel_consumption)
        return [this.emptyValues];

      return [
        {
          value: this.parseComma(
            this.getActualRoute.boiler_fuel_consumption,
            1
          ),
          unit: "L",
        },
      ];
    },
    averageSpeedOvers() {
      return [
        {
          title: "平均対水速力",
          option: "",
          values: this.averageSpeedThroughWater,
          isVarticalValues: false,
        },
        {
          title: "平均対地速力",
          option: "",
          values: this.averageSpeedOverGround,
          isVarticalValues: false,
        },
      ];
    },
    averageSpeedOverGround() {
      if (
        !this.getActualRoute ||
        !this.getActualRoute.average_speed_over_ground
      )
        return [this.emptyValues];

      return [
        {
          value: this.parseComma(
            this.getActualRoute.average_speed_over_ground,
            2
          ),
          unit: "kt",
        },
      ];
    },
    averageSpeedThroughWater() {
      if (
        !this.getActualRoute ||
        !this.getActualRoute.average_speed_through_water
      )
        return [this.emptyValues];

      return [
        {
          value: this.parseComma(
            this.getActualRoute.average_speed_through_water,
            2
          ),
          unit: "kt",
        },
      ];
    },
    operationRateParams() {
      return [
        {
          title: "主機稼働率",
          values: this.operationRateValues,
          isVarticalValues: true,
        },
      ];
    },

    operationRateValues() {
      if (!this.getOperationRate) return [this.emptyValues];

      return [
        {
          value: Math.round(this.getOperationRate),
          unit: "％",
        },
      ];
    },
    ghgParams() {
      return [
        {
          title: "GHG",
          values: this.ghgValues,
          isVarticalValues: true,
        },
      ];
    },
    ghgValues() {
      if (!this.getActualRoute || !this.getActualRoute.ghg)
        return [this.emptyValues];

      return [
        {
          value: this.parseComma(Math.round(this.getActualRoute.ghg), 0),
          unit: "kg-co2",
        },
      ];
    },
  },
  methods: {
    parseComma(value, round) {
      if (typeof value !== "number") return "-";
      if (value) {
        return parseFloat(value.toFixed(round)).toLocaleString();
      } else {
        return "-";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@media print {
  .main {
    float: left;
    width: 48%;
  }
  .sub {
    float: right;
    width: 48%;
  }
}</style
>s
